import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { fighters, type ArenaDef } from './content'
import { stageObjectDef } from './arenaStage'
import { useFightStore, type ImpactCue } from './store'
import type { AnimationVariationPlan } from './animationVariation'
import { SUPREME_ENGINE_CONTRACT_HASH, SUPREME_ENGINE_PROFILE, supremeEngine } from './SupremeGameEngineProfile.generated'

export const SUPREME_GAME_ENGINES = SUPREME_ENGINE_PROFILE.engines.map(engine=>({id:engine.id,name:engine.name+' Engine'}))

const finite=(value:number,fallback=0)=>Number.isFinite(value)?value:fallback
const bounded=(value:number,min:number,max:number,fallback=0)=>THREE.MathUtils.clamp(finite(value,fallback),min,max)
export function resolveEngineAnimationPlan(plan:AnimationVariationPlan|null,names:readonly string[]):AnimationVariationPlan|null{
 const tuning=supremeEngine('animation-integrity').tuning
 const quarantined=new Set(tuning.quarantinedClips.map(name=>name.trim().toLowerCase()))
 const safeNames=names.filter(name=>!quarantined.has(name.trim().toLowerCase()))
 if(!safeNames.length)return null
 if(plan&&safeNames.includes(plan.clip))return{...plan,playbackRate:bounded(plan.playbackRate,tuning.minPlaybackRate,tuning.maxPlaybackRate,1),crossFadeSeconds:bounded(plan.crossFadeSeconds,tuning.minCrossfade,tuning.maxCrossfade,.08)}
 const clip=safeNames.find(name=>/idle|stand|breath|ready|baselayer/i.test(name))??safeNames[0]
 return{clip,semantic:'idle',playbackRate:1,crossFadeSeconds:.08,loop:true,candidateCount:safeNames.length,seed:0}
}

export function stabilizeHeroMotionFrame<T extends {x:number;y:number;z:number;pitch:number;yaw:number;roll:number;scale:number}>(frame:T):T{
 const tuning=supremeEngine('motion-continuity').tuning
 return{...frame,
  x:bounded(frame.x,-tuning.maxPresentationOffset,tuning.maxPresentationOffset),y:bounded(frame.y,-.35,tuning.maxPresentationY),z:bounded(frame.z,-tuning.maxPresentationOffset,tuning.maxPresentationOffset),
  pitch:bounded(frame.pitch,-tuning.maxAngularOffset,tuning.maxAngularOffset),yaw:bounded(frame.yaw,-tuning.maxAngularOffset,tuning.maxAngularOffset),
  roll:bounded(frame.roll,-tuning.maxAngularOffset,tuning.maxAngularOffset),scale:bounded(frame.scale,tuning.minScale,tuning.maxScale,1),
 }
}

function FramePacingEngine(){
 const tuning=supremeEngine('frame-pacing').tuning
 const {scene}=useThree(),ema=useRef(1/60),timer=useRef(0),samples=useRef(0)
 useFrame((_,dt)=>{
  const safe=Math.min(.1,Math.max(1/240,finite(dt,1/60)));ema.current=THREE.MathUtils.lerp(ema.current,safe,.06);timer.current+=safe
  if(timer.current<1)return;timer.current=0;samples.current++
  const fps=1/Math.max(1/240,ema.current)
  scene.userData.inyronFramePacing={fps:Number(fps.toFixed(1)),targetFps:tuning.targetFps,pressure:fps<tuning.targetFps*.55?'critical':fps<tuning.targetFps*.78?'high':'normal',samples:samples.current,writer:'telemetry-only',at:performance.now()}
 })
 return null
}
function CameraGuardianEngine(){
 const tuning=supremeEngine('camera-guardian').tuning
 const {camera}=useThree()
 useFrame(()=>{
  const {player:p,enemy:e}=useFightStore.getState()
  const cx=(p.x+e.x)/2,cz=(p.z+e.z)/2
  if(![camera.position.x,camera.position.y,camera.position.z].every(Number.isFinite))camera.position.set(cx,2.4,cz+10)
  camera.position.y=bounded(camera.position.y,1.35,8,2.4)
  const dx=camera.position.x-cx,dz=camera.position.z-cz,dist=Math.hypot(dx,dz)||1
  if(dist<4.6){const k=4.6/dist;camera.position.x=cx+dx*k;camera.position.z=cz+dz*k}
  else if(dist>Math.min(22,tuning.maxCameraDistance)){const maxDist=Math.min(22,tuning.maxCameraDistance),k=maxDist/dist;camera.position.x=cx+dx*k;camera.position.z=cz+dz*k}
  if(camera instanceof THREE.PerspectiveCamera){
   const fov=bounded(camera.fov,tuning.minFov,Math.min(52,tuning.maxFov),40),near=bounded(camera.near,tuning.minNear,tuning.maxNear,.1),far=Math.max(tuning.minFar,finite(camera.far,120))
   if(fov!==camera.fov||near!==camera.near||far!==camera.far){camera.fov=fov;camera.near=near;camera.far=far;camera.updateProjectionMatrix()}
  }
 })
 return null
}

function ContactFidelityEngine(){
 const impactSerial=useFightStore(s=>s.impactSerial),root=useRef<THREE.Group>(null)
 const snapshot=useFightStore.getState(),cue=snapshot.impactCue,pi=snapshot.playerIndex,ei=snapshot.enemyIndex
 const accent=cue?.source==='player'?fighters[pi]?.accent:fighters[ei]?.accent
 useFrame((_,dt)=>{const live=useFightStore.getState(),liveCue=live.impactCue;if(!root.current||!liveCue)return;root.current.rotation.z+=dt*(liveCue.attackKind==='special'?7:4);root.current.scale.setScalar(1+Math.min(.9,live.impactCueTimer*4))})
 if(!cue||!accent)return null
 const strength=cue.attackKind==='special'?1.35:cue.kind==='guard-break'?1.18:cue.attackKind==='heavy'?1:.78
 return <group ref={root} key={impactSerial} position={[cue.x,1.1,cue.z]} name="contact-fidelity-engine">
  <mesh rotation={[Math.PI/2,0,0]}><ringGeometry args={[.34,.38,40]}/><meshBasicMaterial color={accent} transparent opacity={.72} blending={THREE.AdditiveBlending} depthWrite={false}/></mesh>
  {Array.from({length:8},(_,i)=>{const a=i/8*Math.PI*2;return <mesh key={i} position={[Math.cos(a)*.48,Math.sin(a)*.38,(i%2?.16:-.16)]} rotation={[0,0,a]} scale={[strength,.7,1]}><boxGeometry args={[.28,.022,.018]}/><meshBasicMaterial color={i%3===0?'#ffffff':accent} transparent opacity={.82} blending={THREE.AdditiveBlending} depthWrite={false}/></mesh>})}
 </group>
}
type StageBurst={key:string;position:[number,number,number];color:string;strength:number;createdAt:number}
const materialColor:Record<string,string>={stone:'#d6c4aa',earth:'#b99a72',wood:'#d08b4c',metal:'#9ed7ff',crystal:'#a9f8ff',organic:'#e06b7c',water:'#7dd9ff'}
function DestructionCinematicsEngine(){
 const transitions=useFightStore(s=>s.stageTransitions),[burst,setBurst]=useState<StageBurst|null>(null),seen=useRef(''),root=useRef<THREE.Group>(null),expired=useRef(false)
 useEffect(()=>{
  const t=transitions.at(-1);if(!t)return
  const key=`${t.objectId}:${t.simulationTick}:${t.to}`;if(key===seen.current)return;seen.current=key
  const def=stageObjectDef(t.objectId);if(!def)return
  const strength=t.to==='collapsed'?1.5:t.to==='broken'?1.05:.7
  expired.current=false;setBurst({key,position:def.position,color:def.accent??materialColor[def.material]??'#ffffff',strength,createdAt:performance.now()})
 },[transitions])
 useFrame((_,dt)=>{
  if(!burst||!root.current)return
  const age=(performance.now()-burst.createdAt)/1000,p=Math.min(1,age/.95);root.current.rotation.y+=dt*1.7;root.current.scale.setScalar(.55+p*burst.strength*1.45)
  root.current.traverse(node=>{if(node instanceof THREE.Mesh&&node.material instanceof THREE.MeshBasicMaterial)node.material.opacity=Math.max(0,(1-p)*.78)})
  if(age>=.98&&!expired.current){expired.current=true;setBurst(null)}
 })
 if(!burst)return null
 return <group ref={root} key={burst.key} position={burst.position} name="destruction-cinematics-engine">
  {Array.from({length:12},(_,i)=>{const a=i/12*Math.PI*2,r=.3+(i%4)*.11;return <mesh key={i} position={[Math.cos(a)*r,(i%5)*.12,Math.sin(a)*r]} rotation={[a*.7,a,-a*.45]}><tetrahedronGeometry args={[.08+(i%3)*.025,0]}/><meshBasicMaterial color={i%4===0?'#ffffff':burst.color} transparent opacity={.78} blending={THREE.AdditiveBlending} depthWrite={false}/></mesh>})}
 </group>
}
function AtmosphereCohesionEngine({arena}:{arena:ArenaDef}){
 const {scene}=useThree()
 const baseSky=useMemo(()=>new THREE.Color(arena.sky),[arena.sky]),baseFog=useMemo(()=>new THREE.Color(arena.fog),[arena.fog]),pulse=useMemo(()=>new THREE.Color(),[])
 useFrame(()=>{
  const state=useFightStore.getState(),cue=state.impactCue,timer=state.impactCueTimer,pi=state.playerIndex,ei=state.enemyIndex
  const accent=cue?.source==='player'?fighters[pi]?.accent:fighters[ei]?.accent
  pulse.set(accent??arena.accent)
  const w=cue?Math.min(.18,Math.max(0,timer)*(cue.attackKind==='special'?.72:.44)):0
  if(scene.background instanceof THREE.Color)scene.background.copy(baseSky).lerp(pulse,w*.55)
  if(scene.fog instanceof THREE.Fog||scene.fog instanceof THREE.FogExp2)scene.fog.color.copy(baseFog).lerp(pulse,w)
 })
 return null
}

function CharacterReadabilityEngine(){
 const pi=useFightStore(s=>s.playerIndex),ei=useFightStore(s=>s.enemyIndex),pl=useRef<THREE.PointLight>(null),el=useRef<THREE.PointLight>(null)
 useFrame((_,dt)=>{
  const {player:p,enemy:e}=useFightStore.getState()
  const update=(light:THREE.PointLight|null,state:typeof p)=>{if(!light)return;light.position.x=THREE.MathUtils.damp(light.position.x,state.x,14,dt);light.position.y=THREE.MathUtils.damp(light.position.y,1.55,14,dt);light.position.z=THREE.MathUtils.damp(light.position.z,state.z+.7,14,dt);const danger=1-state.health/Math.max(1,state.maxHealth),active=state.attackPhase?1:0,form=(state as typeof state&{form?:string}).form==='first-light'?1.6:1;light.intensity=THREE.MathUtils.damp(light.intensity,(.75+active*1.5+danger*.75)*form,10,dt)}
  update(pl.current,p);update(el.current,e)
 })
 return <group name="character-readability-engine"><pointLight ref={pl} color={fighters[pi]?.accent??'#ffffff'} distance={4.5} decay={2}/><pointLight ref={el} color={fighters[ei]?.accent??'#ffffff'} distance={4.5} decay={2}/></group>
}

function transient(ctx:AudioContext,bus:AudioNode,cue:ImpactCue,weight:number){
 const osc=ctx.createOscillator(),gain=ctx.createGain(),pan=ctx.createStereoPanner(),now=ctx.currentTime,freq=cue.kind==='parry'?1180:cue.attackKind==='special'?52:cue.kind==='guard-break'?68:86
 osc.type=cue.kind==='parry'?'sine':'triangle';osc.frequency.setValueAtTime(freq,now);osc.frequency.exponentialRampToValueAtTime(Math.max(32,freq*.72),now+.16)
 gain.gain.setValueAtTime(.0001,now);gain.gain.exponentialRampToValueAtTime(supremeEngine('sonic-dynamics').tuning.transientGain*weight,now+.006);gain.gain.exponentialRampToValueAtTime(.0001,now+.2)
 pan.pan.value=THREE.MathUtils.clamp(cue.x/7,-1,1);osc.connect(gain).connect(pan).connect(bus);osc.start(now);osc.stop(now+.22)
}
function SonicDynamicsEngine(){
 useEffect(()=>{
  let ctx:AudioContext|null=null,bus:GainNode|null=null,compressor:DynamicsCompressorNode|null=null
  const unlock=()=>{if(!ctx){const t=supremeEngine('sonic-dynamics').tuning;ctx=new AudioContext();bus=ctx.createGain();compressor=ctx.createDynamicsCompressor();compressor.threshold.value=t.thresholdDb;compressor.knee.value=t.kneeDb;compressor.ratio.value=t.ratio;compressor.attack.value=t.attack;compressor.release.value=t.release;bus.gain.value=.72;bus.connect(compressor).connect(ctx.destination)}if(ctx.state==='suspended')void ctx.resume().catch(()=>undefined)}
  window.addEventListener('pointerdown',unlock,{passive:true})
  const unsub=useFightStore.subscribe((s,prev)=>{
   if(!ctx||ctx.state!=='running'||!bus||s.impactSerial===prev.impactSerial||!s.impactCue)return
   const cue=s.impactCue,weight=cue.stageBreakState==='collapsed'?1.5:cue.attackKind==='special'?1.35:cue.kind==='guard-break'?1.2:cue.kind==='parry'?.85:.72
   transient(ctx,bus,cue,weight)
  })
  return()=>{unsub();window.removeEventListener('pointerdown',unlock);if(ctx)void ctx.close().catch(()=>undefined)}
 },[])
 return null
}

function MaterialAssetSentinelEngine(){
 const tuning=supremeEngine('material-asset-sentinel').tuning
 const {scene,gl}=useThree(),timer=useRef(0),repairs=useRef(0),stack=useRef<THREE.Object3D[]>([]),active=useRef(false),scans=useRef(0)
 const anisotropy=useRef(1)
 useEffect(()=>{
  scene.userData.inyronEngineHealth={revision:SUPREME_ENGINE_PROFILE.revision,hash:SUPREME_ENGINE_CONTRACT_HASH,engineCount:SUPREME_GAME_ENGINES.length,materialRepairs:repairs.current,lastScan:0,incremental:true}
 },[scene])
 useFrame((_,dt)=>{
  timer.current+=Math.max(0,dt)
  const pressure=(scene.userData.inyronPerformance as {pressure?:string}|undefined)?.pressure
  if(!active.current&&(pressure==='high'||pressure==='critical'))return
  if(active.current&&pressure==='critical')return
  if(!active.current&&timer.current>=tuning.scanIntervalSeconds){timer.current=0;active.current=true;stack.current=[scene];anisotropy.current=Math.min(tuning.maxAnisotropy,gl.capabilities.getMaxAnisotropy())}
  if(!active.current)return
  let budget=pressure==='high'?4:14
  while(budget-->0&&stack.current.length){
   const object=stack.current.pop()!;for(let i=0;i<object.children.length;i++)stack.current.push(object.children[i])
   const vectors=[object.position,object.scale]
   for(const vector of vectors){if(!Number.isFinite(vector.x))vector.x=object===scene?0:1;if(!Number.isFinite(vector.y))vector.y=object===scene?0:1;if(!Number.isFinite(vector.z))vector.z=object===scene?0:1}
   if(!Number.isFinite(object.rotation.x))object.rotation.x=0;if(!Number.isFinite(object.rotation.y))object.rotation.y=0;if(!Number.isFinite(object.rotation.z))object.rotation.z=0
   if(!(object instanceof THREE.Mesh))continue
   if(!object.geometry.boundingSphere)try{object.geometry.computeBoundingSphere()}catch{}
   const materials=Array.isArray(object.material)?object.material:[object.material]
   for(const material of materials)if(material instanceof THREE.MeshStandardMaterial){
    if(!Number.isFinite(material.roughness)){material.roughness=.55;repairs.current++}
    if(!Number.isFinite(material.metalness)){material.metalness=.1;repairs.current++}
    if(!Number.isFinite(material.envMapIntensity)||material.envMapIntensity<0){material.envMapIntensity=1;repairs.current++}
    if(material.map&&material.map.anisotropy<anisotropy.current){material.map.anisotropy=anisotropy.current;material.map.needsUpdate=true;repairs.current++}
   }
  }
  if(stack.current.length)return
  active.current=false;scans.current++
  scene.userData.inyronEngineHealth={revision:SUPREME_ENGINE_PROFILE.revision,hash:SUPREME_ENGINE_CONTRACT_HASH,engineCount:SUPREME_GAME_ENGINES.length,materialRepairs:repairs.current,lastScan:performance.now(),scans:scans.current,incremental:true}
 })
 return null
}
export function SupremeGameEngines({arena}:{arena:ArenaDef}){
 return <group name="inyron-supreme-game-engines">
  <FramePacingEngine/>
  <CameraGuardianEngine/>
  <ContactFidelityEngine/>
  <DestructionCinematicsEngine/>
  <AtmosphereCohesionEngine arena={arena}/>
  <CharacterReadabilityEngine/>
  <SonicDynamicsEngine/>
  <MaterialAssetSentinelEngine/>
 </group>
}
